import { TimelineEvent } from "@deligoo/shared"
import dayjs from "dayjs"
import localForage from "localforage"
import { toast } from "react-toastify"

import { globalStore } from "@/App"
import { OrdersSuggestions } from "@/components/Header/OrdersSearch"
import { Site } from "@/sites"
import { clearDataOnLogout } from "@/store/user"
import {
  AssignOrderProps,
  CancelOrderProps,
  Client,
  DeliveryZone,
  Driver,
  Errors,
  ManualOrder,
  MoveOrderToBufferProps,
  Notification,
  Order,
  OrderFull,
  OrderReturnProps,
  OrderWithoutPayment,
  Payment,
  PaymentForm,
  PaymentsProps,
  ReassignOrderProps,
  SetTemporaryDistanceLimitProps,
  SetTemporaryEndTimeProps,
  Shift,
  Subteam,
  Team,
  TeamStats,
  User,
} from "@/types"

import { retrieveSavedToken } from "./utils"

// Endpoints are to be switched to 'appHost' and 'appPort' eventually
export function getApiURL({ appHost, appPort, protocol }: Site) {
  return `${protocol}://${appHost}${appPort ? `:${appPort}` : ""}/api/logistics_app/v1`
}

async function fetchFromApi<T extends unknown>(endpoint: string, options: RequestInit = {}) {
  const apiUrl = await localForage.getItem<string>("api_url")
  const token = await retrieveSavedToken()

  const response = await fetch(`${apiUrl}${endpoint}`, {
    ...options,
    headers: {
      Authorization: token!,
      Accept: "application/json",
      "Content-Type": "application/json",
      ...options.headers,
    },
  })

  if (response.status === 401) {
    const {
      user: { isAuthenticated },
    } = globalStore.getState()

    if (isAuthenticated) {
      await localForage.clear()
      globalStore.dispatch(clearDataOnLogout())
      sessionStorage.clear()

      toast.error("Nastąpiło wylogowanie z powodu wygasłej sesji")
    }
  }

  const json: T = await response.json()

  return { response, json }
}

// POST /logistics_app/v1/sign_in
export function fetchSignIn(email: string, password: string) {
  return fetchFromApi<{ data?: User; errors?: Errors }>("/sign_in", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  })
}

// DELETE /logistics_app/v1/sign_out
export function fetchSignOut() {
  return fetchFromApi("/sign_out", { method: "DELETE" })
}

// GET /logistics_app/v1/teams
export function fetchTeams() {
  return fetchFromApi<{ data?: Team[] }>("/teams")
}

// GET /logistics_app/v1/notifications
export function fetchNotifications(teamsUuids: Array<Team["uuid"]>) {
  const params = new URLSearchParams({ date: dayjs().format("YYYY-MM-DD") })
  teamsUuids.forEach((teamUuid) => params.append("team_uuids[]", teamUuid.toString()))

  // TODO backend: date param should not be necessary
  return fetchFromApi<{ data?: Notification[] }>(`/notifications?${params}`)
}

// PUT /logistics_app/v1/notifications/${id}/read
export function fetchMarkNotificationAsRead(id: Notification["id"]) {
  return fetchFromApi<{ data?: Notification; errors?: Errors }>(`/notifications/${id}/read`, {
    method: "PUT",
  })
}

// GET /logistics_app/v1/teams/statistics
export function fetchTeamsStats(teamsIds: Array<Team["id"]>) {
  const params = new URLSearchParams()
  teamsIds.forEach((teamId) => params.append("team_ids[]", teamId.toString()))

  return fetchFromApi<{ data?: TeamStats[] }>(`/teams/statistics?${params}`)
}

// POST /logistics_app/v1/orders
export function fetchCreateOrder(body: Partial<ManualOrder>) {
  return fetchFromApi<{
    data?: Order
    errors?: Errors
  }>(`/orders`, {
    method: "POST",
    body: JSON.stringify({ form_data: body }),
  })
}

// GET /logistics_app/v1/orders
export function fetchOrders(teamId: Team["id"], date?: string) {
  const params = new URLSearchParams({ team_id: teamId.toString() })
  if (date) params.append("date", date)

  return fetchFromApi<{ data?: Order[] }>(`/orders?${params}`)
}

// GET /logistics_app/v1/orders/${id}
export function fetchOrder(orderId: OrderFull["id"], teamId: Team["id"]) {
  return fetchFromApi<{ data: OrderFull }>(`/orders/${orderId}?team_id=${teamId}`)
}

// GET /logistics_app/v1/orders/${id}
export function fetchOrderTimeline(teamId: Team["id"], orderId: Order["id"]) {
  return fetchFromApi<{ data?: OrderFull }>(`/orders/${orderId}?team_id=${teamId}`)
}

// GET /logistics_app/v1/orders/without_payments
export function fetchOrdersWithoutPayments(teamId: Team["id"]) {
  return fetchFromApi<{ data?: OrderWithoutPayment[]; errors?: Errors }>(`/orders/without_payments?team_id=${teamId}`)
}

// PUT /logistics_app/v1/orders/${id}/toggle_payment_status
export function fetchTogglePaymentStatus(orderId: Order["id"]) {
  return fetchFromApi<{ data?: OrderWithoutPayment; errors?: Errors }>(`/orders/${orderId}/toggle_payment_status`, {
    method: "PUT",
  })
}

// PUT /logistics_app/v1/orders/${id}
export function fetchEditOrder(teamId: Team["id"], orderId: Order["id"], body: Partial<ManualOrder>) {
  return fetchFromApi<{ data?: OrderFull; errors?: Errors }>(`/orders/${orderId}`, {
    method: "PUT",
    body: JSON.stringify({
      form_data: {
        team_id: teamId,
        ...body,
      },
    }),
  })
}

// GET /logistics_app/v1/orders/search?uid=${uid}
export function fetchSearchOrders(uid: string) {
  return fetchFromApi<{ data?: OrdersSuggestions; errors?: Errors }>(`/orders/search?uid=${uid}`)
}

// GET /logistics_app/v1/drivers
export function fetchDrivers(teamsIds: Array<Team["id"]>, date?: string) {
  const params = new URLSearchParams()
  teamsIds.forEach((teamId) => params.append("team_ids[]", teamId.toString()))
  if (date) params.append("date", date)

  return fetchFromApi<{ data?: Driver[] }>(`/drivers?${params}`)
}

// PUT /logistics_app/v1/drivers/{id}/change_driver_activity
export function fetchSetDriverActivity(id: Driver["id"], body: { is_available: Driver["is_available"] }) {
  return fetchFromApi<{ data?: Driver }>(`/drivers/${id}/change_driver_activity`, {
    method: "PUT",
    body: JSON.stringify(body),
  })
}

// POST /logistics_app/v1/drivers/{id}/temporary_break
export function fetchSetTemporaryBreak(id: Driver["id"], start_time: string, reason: string) {
  return fetchFromApi<{ data?: Driver; errors?: Errors }>(`/drivers/${id}/temporary_break`, {
    method: "POST",
    body: JSON.stringify({
      start_time,
      reason,
    }),
  })
}

// DELETE /logistics_app/v1/drivers/{driverId}/breaks/{breakId}
export function fetchDeleteBreakById(driverId: Driver["id"], breakId: number) {
  return fetchFromApi<{ data?: Driver; errors?: Errors }>(`/drivers/${driverId}/breaks/${breakId}`, {
    method: "DELETE",
  })
}

// POST /logistics_app/v1/drivers/{id}/break
export function fetchSetNewBreak(id: Driver["id"], start_time: string, duration: number, reason: string) {
  return fetchFromApi<{ data?: Driver; errors?: Errors }>(`/drivers/${id}/break`, {
    method: "POST",
    body: JSON.stringify({
      start_time,
      duration,
      reason,
    }),
  })
}

// GET /logistics_app/v1/drivers/{id}/shifts
export function fetchDriverShiftsById(id: Driver["id"]) {
  return fetchFromApi<{ data?: { shifts: Shift[] }; errors?: Errors }>(`/drivers/${id}/shifts`)
}

// PUT /logistics_app/v1/drivers/{id}/shifts
export function fetchSetShifts(id: Driver["id"], shifts: Shift[]) {
  return fetchFromApi<{ data?: Driver; errors?: Errors }>(`/drivers/${id}/shifts`, {
    method: "PUT",
    body: JSON.stringify({
      shifts,
    }),
  })
}

// PUT /logistics_app/v1/drivers/{id}/change_transport_type
export function fetchChangeTransportType(id: Driver["id"], transport_type: Driver["transport_type"]) {
  return fetchFromApi<{ data?: Driver; errors?: Errors }>(`/drivers/${id}/change_transport_type`, {
    method: "PUT",
    body: JSON.stringify({
      transport_type,
    }),
  })
}

// GET /logistics_app/v1/drivers/{id}/events
export function fetchDriverEventsById(id: Driver["id"], date: string) {
  const params = new URLSearchParams()
  params.append("start_date", date)
  params.append("end_date", date)

  return fetchFromApi<{ data?: TimelineEvent[]; errors?: Errors }>(`/drivers/${id}/events?${params}`)
}

// PUT /logistics_app/v1/drivers/{id}/sub_teams
export function fetchUpdateDriverSubteams(id: Driver["id"], sub_team_ids: Array<Subteam["id"]>) {
  return fetchFromApi<{ data?: Driver; errors: Errors }>(`/drivers/${id}/sub_teams`, {
    method: "PUT",
    body: JSON.stringify({
      sub_team_ids,
    }),
  })
}

// GET /logistics_app/v1/clients
export function fetchClients(teamsIds: Array<Team["id"]>) {
  const params = new URLSearchParams()
  teamsIds.forEach((teamId) => params.append("team_ids[]", teamId.toString()))

  return fetchFromApi<{ data?: Client[] }>(`/clients?${params}`)
}

// GET /logistics_app/v1/me
export function fetchCurrentUser() {
  return fetchFromApi<{ data?: User }>("/me")
}

//PUT /logistics_app/v1/clients/{id}/set_temporary_distance_limit
export function fetchSetTemporaryDistanceLimit({ clientId, body }: SetTemporaryDistanceLimitProps) {
  return fetchFromApi<{ data?: Client; errors?: Errors }>(`/clients/${clientId}/set_temporary_distance_limit`, {
    method: "PUT",
    body: JSON.stringify(body),
  })
}

//PUT /logistics_app/v1/clients/{id}/set_temporary_end_time_options
export function fetchSetTemporaryEndTime({ clientId, body }: SetTemporaryEndTimeProps) {
  return fetchFromApi<{ data?: Client; errors?: Errors }>(`/clients/${clientId}/set_temporary_end_time_options`, {
    method: "PUT",
    body: JSON.stringify(body),
  })
}

// POST /logistics_app/v1/clients/{id}/sign_in
export function fetchSignInClient(clientId: Client["id"]) {
  return fetchFromApi<{
    data: {
      pos_admin_auth_token: string
      pos_admin_auth_host: string
      redirect_url: string
    }
  }>(`/clients/${clientId}/sign_in`, {
    method: "POST",
  })
}

// PUT /logistics_app/v1/drivers/{id}/change_payment_method
export function fetchSetDriverPaymentForms(
  driverId: Driver["id"],
  body: { accepted_payment_forms: Driver["payment_forms"] }
) {
  return fetchFromApi<{ data?: Driver; errors?: Errors }>(`/drivers/${driverId}/change_payment_method`, {
    method: "PUT",
    body: JSON.stringify(body),
  })
}

// PUT /logistics_app/v1/drivers/{id}/temporarily_turn_off_payment_method
export function fetchSetTemporaryDriverPaymentForms(
  driverId: Driver["id"],
  body: { payment_form: NonNullable<PaymentForm>; disable_time: number | null }
) {
  return fetchFromApi<{ data?: Driver; errors?: Errors }>(`/drivers/${driverId}/temporarily_turn_off_payment_method`, {
    method: "PUT",
    body: JSON.stringify(body),
  })
}

// PUT /logistics_app/v1/orders/{id}/reassign
export function fetchReassignOrder({ orderId, body }: ReassignOrderProps) {
  return fetchFromApi<{ data?: Order; errors?: Errors }>(`/orders/${orderId}/reassign`, {
    method: "PUT",
    body: JSON.stringify(body),
  })
}

// PUT /logistics_app/v1/orders/{id}/cancel
export function fetchCancelOrder({ orderId, body }: CancelOrderProps) {
  return fetchFromApi<{ data?: Order; errors?: Errors }>(`/orders/${orderId}/cancel`, {
    method: "PUT",
    body: JSON.stringify(body),
  })
}

// POST /logistics_app/v1/orders/{id}/return
export function fetchOrderReturn({ orderId, body }: OrderReturnProps) {
  return fetchFromApi<{ data?: Order; errors?: Errors }>(`/orders/${orderId}/return`, {
    method: "POST",
    body: JSON.stringify(body),
  })
}

// PUT /logistics_app/v1/orders/{id}/move_to_buffer
export function fetchMoveOrderToBuffer({ orderId, body }: MoveOrderToBufferProps) {
  return fetchFromApi<{ data?: Order; errors?: Errors }>(`/orders/${orderId}/move_to_buffer`, {
    method: "PUT",
    body: JSON.stringify(body),
  })
}

// PUT /logistics_app/v1/orders/{id}/move_to_assignment_queue
export function fetchOrderAssign(orderId: Order["id"]) {
  return fetchFromApi<{ data?: Order; errors?: Errors }>(`/orders/${orderId}/move_to_assignment_queue`, {
    method: "PUT",
  })
}

// GET /logistics_app/v1/drivers/for_reassignment?order_id={orderId}
export function fetchDriversForReassignment(orderId: Order["id"]) {
  return fetchFromApi<{ data?: Driver[]; errors?: Errors }>(`/drivers/for_reassignment?order_id=${orderId}`)
}

// GET /logistics_app/v1/payments
export function fetchPayments({ teamId, params }: PaymentsProps) {
  const searchParams = new URLSearchParams()

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value) searchParams.append(key, value)
    })
  }

  const queryString = searchParams ? `&${searchParams.toString()}` : ""

  return fetchFromApi<{ data?: Payment[]; errors?: Errors }>(`/payments?team_id=${teamId}${queryString}`)
}

// GET /logistics_app/v1/payments/unsettled
export function fetchUnsettledPayments(teamId: Team["id"]) {
  return fetchFromApi<{ data?: Payment[]; errors?: Errors }>(`/payments/unsettled?team_id=${teamId}`)
}

// POST /logistics_app/v1/payments/sync
export function fetchSyncPayments(body: { team_id: Team["id"] }) {
  return fetchFromApi<{ errors?: Errors }>(`/payments/sync`, {
    method: "POST",
    body: JSON.stringify(body),
  })
}

// DELETE /logistics_app/v1/payments/{id}
export function fetchDeletePayment(paymentId: Payment["id"]) {
  return fetchFromApi(`/payments/${paymentId}`, {
    method: "DELETE",
  })
}

// PUT /logistics_app/v1/payments/{id}/set_as_a_tip
export function fetchSetPaymentAsTip(paymentId: Payment["id"]) {
  return fetchFromApi<{ data?: Payment; errors?: Errors }>(`/payments/${paymentId}/set_as_a_tip`, {
    method: "PUT",
  })
}

// PUT /logistics_app/v1/payments/{transaction_code}/assign_order
export function fetchAssignOrderToTransaction({ transactionCode, body }: AssignOrderProps) {
  return fetchFromApi<{ data?: Payment; errors?: Errors }>(`/payments/${transactionCode}/assign_order`, {
    method: "PUT",
    body: JSON.stringify(body),
  })
}

// GET /logistics_app/v1/teams/{id}/delivery_zone
export function fetchTeamDeliveryZone(teamId: Team["id"]) {
  return fetchFromApi<{
    data?: DeliveryZone
    errors?: Errors
  }>(`/teams/${teamId}/delivery_zone`)
}

// GET /logistics_app/v1/clients/{id}/delivery_zone
export function fetchClientDeliveryZone(clientId: Client["id"]) {
  return fetchFromApi<{
    data?: DeliveryZone
    errors?: Errors
  }>(`/clients/${clientId}/delivery_zone`)
}
